import {createSelector} from 'reselect';

import {ApplicationStoreType} from 'reducers';
import {totalsIsFulfilled} from 'pages/postPurchaseInsurance/store/totals/selectors';

import {MODULE_KEY} from '../../constants';
import {
    PostPurchaseInsuranceOrderType,
    ProductType,
    OrderStatusType,
    PostInsurancePeriodType,
    InsuranceType,
    InsurancePricesType,
} from './types';

const MIN_PRICE = 100;

/**
 * Return order local state
 * @param {ApplicationStoreType} store - application store
 * @return {PostPurchaseInsuranceOrderType} - order local state
 */
export const getStateOrder = (store: ApplicationStoreType): PostPurchaseInsuranceOrderType =>
    store[MODULE_KEY].order;

/**
 * Return order status
 * @param {ApplicationStoreType} store - application store
 * @return {OrderStatusType} - order status
 */
export const getOrderStatus = createSelector(
    getStateOrder,
    (state): OrderStatusType => state.orderStatus,
);

/**
 * Returns offer ID
 * @param {ApplicationStoreType} store - application store
 * @return {string} - offer ID
 */
export const getOfferId = createSelector(getStateOrder, (state): string => state.offerId);

/**
 * Return all fetched products without insurance in order
 * @param {ApplicationStoreType} store - application store
 * @return {Product[]} - product
 */
export const getProducts = createSelector(getStateOrder, (state): ProductType[] => state.products);

/**
 * Return only selected products
 * @param {ApplicationStoreType} store - application store
 * @return {Product[]} - selected products
 */
export const getSelectedProducts = createSelector(getProducts, (products): ProductType[] => {
    return products.filter(product => product.selected);
});

/**
 * Return selected insurance
 * @param {ApplicationStoreType} store - application store
 * @return {PostInsurancePeriodType} - selected insurance
 */
export const getInsurancePeriod = createSelector(
    getStateOrder,
    (state): PostInsurancePeriodType => state.insurancePeriod,
);

/**
 * Return insurance from product by insuranceId
 * @param {Product} product - product
 * @param {PostInsurancePeriodType} insurancePeriod - period
 * @return {InsuranceType | void} - found insurance
 */
export const getSelectedInsurance = (
    product: ProductType,
    insurancePeriod: PostInsurancePeriodType,
): InsuranceType | void => {
    return product.insurances.find(insurance => insurance.period === insurancePeriod);
};

/**
 * Return array with insurance periods
 * @param {ApplicationStoreType} store - application store
 * @return {number[]} - insurance periods
 */
export const getInsurancePeriods = createSelector(getProducts, (products): number[] => {
    const [product] = products;

    return product ? product.insurances.map(insurance => insurance.period) : [];
});

/**
 * Return insurances with calculated prices by selected products
 * @param {ApplicationStoreType} store - application store
 * @return {number[]} - insurance periods
 */
export const getInsuranceWithPrices = createSelector(
    getSelectedProducts,
    getInsurancePeriods,
    (products, periods): InsurancePricesType[] => {
        return periods.map(period =>
            products.reduce(
                // eslint-disable-next-line max-nested-callbacks
                (acc, product): InsurancePricesType => {
                    const insurance = getSelectedInsurance(product, period);

                    return {
                        ...acc,
                        price: insurance ? acc.price + insurance.price * product.qty : 0,
                        finalPrice: insurance
                            ? acc.finalPrice + insurance.finalPrice * product.qty
                            : 0,
                    };
                },
                {
                    period,
                    price: 0,
                    finalPrice: 0,
                },
            ),
        );
    },
);

/**
 * Return total insurance price
 * @param {ApplicationStoreType} store - application store
 * @return {number} - total price
 */
export const getTotalPrice = createSelector(
    getSelectedProducts,
    getInsurancePeriod,
    (products, insurancePeriod): number =>
        products.reduce((sum, product) => {
            const insurance = getSelectedInsurance(product, insurancePeriod);

            return sum + (insurance ? insurance.finalPrice * product.qty : 0);
        }, 0),
);

/**
 * Need to hide prices?
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} - need to hide prices?
 */
export const hidePrices = createSelector(getProducts, (products): boolean =>
    products.every(product => MIN_PRICE >= product.price),
);

/**
 * Has several products without insurance in order
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} - Has several products?
 */
export const hasMultiProducts = createSelector(
    getProducts,
    (products): boolean => products.length > 1,
);

/**
 * Order is fulfilled
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} - is fulfilled?
 */
export const orderIsFulfilled = createSelector(
    getStateOrder,
    (state): boolean => state.status === 'fulfilled',
);

/**
 * Order is pending
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} - is pending?
 */
export const orderIsPending = createSelector(
    getStateOrder,
    (state): boolean => state.status === 'pending',
);

/**
 * Order step is completed
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} - is completed?
 */
export const orderIsCompleted = createSelector(
    getSelectedProducts,
    totalsIsFulfilled,
    (products, totalsIsReady) => Boolean(products.length) && totalsIsReady,
);

/**
 * Returns order ID
 * @param {ApplicationStoreType} store - application store
 * @return {string} order ID
 */
export const getOrderId = createSelector(getStateOrder, (state): string => state.orderId);

/**
 * Has a customer some selected products for coverage?
 */
export const hasSelectedProducts = createSelector(getSelectedProducts, products =>
    Boolean(products.length),
);
